import Participant from "../../entities/participant";

/**
 * Participant actions
 *
 * Call order:
 *
 * PARTICIPANT_SAVE_QUEUE:	adds the item to the save queue
 * --> PARTICIPANT_SAVE:	modifies the state
 * --> PARTICIPANT_SAVE_SUCCESS: if all went well
 * --> PARTICIPANT_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail PARTICIPANT_SAVE_QUEUE is during the call to PARTICIPANT_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at PARTICIPANT_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: PARTICIPANT_SAVE_SUCCESS, PARTICIPANT_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or PARTICIPANT_PENDING_CHECK (it left the queue).
 */

export const PARTICIPANT_FETCH_ONE_QUEUE: any = 'PARTICIPANT_FETCH_ONE_QUEUE';
export const PARTICIPANT_FETCH_SUCCESS: any = 'PARTICIPANT_FETCH_SUCCESS';
export const PARTICIPANT_FETCH_FAILURE: any = 'PARTICIPANT_FETCH_FAILURE';
export const PARTICIPANT_FETCH_ONE_SUCCESS: any = 'PARTICIPANT_FETCH_ONE_SUCCESS';
export const PARTICIPANT_FETCH_ONE_FAILURE: any = 'PARTICIPANT_FETCH_ONE_FAILURE';

export const PARTICIPANT_SAVE: any = 'PARTICIPANT_SAVE';
export const PARTICIPANT_SAVE_QUEUE: any = 'PARTICIPANT_SAVE_QUEUE';
export const PARTICIPANT_SAVE_SUCCESS: any = 'PARTICIPANT_SAVE_SUCCESS';
export const PARTICIPANT_SAVE_FAILURE: any = 'PARTICIPANT_SAVE_FAILURE';

export const PARTICIPANT_SAVE_MANY: any = 'PARTICIPANT_SAVE_MANY';
export const PARTICIPANT_SAVE_MANY_QUEUE: any = 'PARTICIPANT_SAVE_MANY_QUEUE';
export const PARTICIPANT_SAVE_MANY_SUCCESS: any = 'PARTICIPANT_SAVE_MANY_SUCCESS';
export const PARTICIPANT_SAVE_MANY_FAILURE: any = 'PARTICIPANT_SAVE_MANY_FAILURE';

export const PARTICIPANT_REMOVE: any = 'PARTICIPANT_REMOVE';
export const PARTICIPANT_REMOVE_QUEUE: any = 'PARTICIPANT_REMOVE_QUEUE';
export const PARTICIPANT_REMOVE_SUCCESS: any = 'PARTICIPANT_REMOVE_SUCCESS';
export const PARTICIPANT_REMOVE_FAILURE: any = 'PARTICIPANT_REMOVE_FAILURE';

export const PARTICIPANT_RESTORE: any = 'PARTICIPANT_RESTORE';
export const PARTICIPANT_RESTORE_QUEUE: any = 'PARTICIPANT_RESTORE_QUEUE';
export const PARTICIPANT_RESTORE_SUCCESS: any = 'PARTICIPANT_RESTORE_SUCCESS';
export const PARTICIPANT_RESTORE_FAILURE: any = 'PARTICIPANT_RESTORE_FAILURE';

export const PARTICIPANT_RESTORE_MANY: any = 'PARTICIPANT_RESTORE_MANY';
export const PARTICIPANT_REMOVE_MANY: any = 'PARTICIPANT_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const PARTICIPANT_SAVE_REVERT: any = 'PARTICIPANT_SAVE_REVERT';
export const PARTICIPANT_PENDING_CHECK: any = 'PARTICIPANT_PENDING_CHECK';

/* Fetch actions */
export const fetchParticipantSuccess = (participants: Participant[], partial: boolean, lastFetch: number) => ({type: PARTICIPANT_FETCH_SUCCESS, participants, partial, lastFetch});
export const fetchParticipantFailure = (err: any) => {
		console.error('ParticipantAction: fetchParticipantFailure', err);
		return {type: PARTICIPANT_FETCH_FAILURE, err};
};
export const fetchOneParticipantQueue = (participantId: string, sentAddedOn?: number) => ({type: PARTICIPANT_FETCH_ONE_QUEUE, participantId, sentAddedOn});
export const fetchOneParticipantSuccess = (participant: Participant | null, sentAddedOn?: number, participantId: string = participant?.id) => ({type: PARTICIPANT_FETCH_ONE_SUCCESS, participantId, participant, sentAddedOn});
export const fetchOneParticipantFailure = (id: string, err: any) => {
		console.error('ParticipantAction: fetchOneParticipantFailure', err);
		return {type: PARTICIPANT_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveParticipant = (participant: Participant) => {
    // console.debug('⮂ PARTICIPANT_SAVE action', participant?.id);
    return {type: PARTICIPANT_SAVE, participant};
}
export const saveParticipantQueue = (participant: Participant, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ PARTICIPANT_SAVE_QUEUE action', participant?.id);
    return {type: PARTICIPANT_SAVE_QUEUE, participant, onQueued};
}
export const saveParticipantSuccess = (participant: Participant, sentAddedOn?: number) => {
    // console.debug('⮂ PARTICIPANT_SAVE_SUCCESS action', participant?.id);
    return {type: PARTICIPANT_SAVE_SUCCESS, participant, sentAddedOn};
}
export const saveParticipantFailure = (participant: Participant, status: number, message: string) => {
    console.warn(`⮂ PARTICIPANT_SAVE_FAILURE error saving participant: HTTP ${status}: ${message}`, participant?.id);
    return {type: PARTICIPANT_SAVE_FAILURE, participant, status, message};
};

/* SaveMany actions */
export const saveManyParticipants = (participants: Participant[]) => {
    // console.debug('⮂ PARTICIPANT_SAVE_MANY action', participants?.map(participant => participant?.id).join(','));
    return {type: PARTICIPANT_SAVE_MANY, participants};
}
export const saveManyParticipantsQueue = (participants: Participant[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ PARTICIPANT_SAVE_MANY_QUEUE action', participants?.map(participant => participant.id).join(','));
    return {type: PARTICIPANT_SAVE_MANY_QUEUE, participants, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `participants` (OWA-1018) */
export const saveManyParticipantsSuccess = (participants: Participant[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ PARTICIPANT_SAVE_MANY_SUCCESS action', participants?.map(participant => participant.id).join(','));
    return {type: PARTICIPANT_SAVE_MANY_SUCCESS, participants, sentAddedOns};
}
export const saveManyParticipantsFailure = (participants: Participant[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_PARTICIPANTS_FAILURE Error saving participants: HTTP ${status}: ${message}`, participants?.map(participant => participant.id).join(','));
		return {type: PARTICIPANT_SAVE_MANY_FAILURE, participants, status, message};
};

/* Remove actions */
export const removeParticipant = (participant: Participant) => ({ type: PARTICIPANT_REMOVE, participant });
export const removeParticipantQueue = (participant: Participant) => ({ type: PARTICIPANT_REMOVE_QUEUE, participant });
export const removeParticipantSuccess = (participant: Participant) => ({ type: PARTICIPANT_REMOVE_SUCCESS, participant });
export const removeParticipantFailure = (participant: Participant, status: number, message: string) => {
	console.error(`Redux: removeParticipantFailure Error removing participant: HTTP ${status}: ${message}`);
	return ({ type: PARTICIPANT_REMOVE_FAILURE, participant, status, message });
};

/* Restore actions */
export const restoreParticipant = (participant: Participant) => ({ type: PARTICIPANT_RESTORE, participant });
export const restoreParticipantQueue = (participant: Participant) => ({ type: PARTICIPANT_RESTORE_QUEUE, participant });
export const restoreParticipantSuccess = (participant: Participant, sentAddedOn?: number) => ({ type: PARTICIPANT_RESTORE_SUCCESS, participant, sentAddedOn });
export const restoreParticipantFailure = (participant: Participant, status: number, message: string) => {
	console.error(`Redux: restoreParticipantFailure Error restoring participant: HTTP ${status}: ${message}`);
	return ({ type: PARTICIPANT_RESTORE_FAILURE, participant, status, message });
};

/* RestoreMany actions */
export const restoreManyParticipants = (participants: Participant[]) => ({ type: PARTICIPANT_RESTORE_MANY, participants });

/* RemoveMany actions */
export const removeManyParticipants = (participants: Participant[]) => ({ type: PARTICIPANT_REMOVE_MANY, participants });

/* Pending save actions (OWA-1018) */
/**
 * The save `participant` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertParticipantSave = (participant: Participant, options: {sentAddedOn?: number, current?: Participant | null, keep?: boolean} = {}) =>
    ({type: PARTICIPANT_SAVE_REVERT, participant, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkParticipantPendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: PARTICIPANT_PENDING_CHECK, live, refused, readAt});